import { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { 
  Search, 
  Calendar, 
  ShoppingCart, 
  Heart, 
  ArrowRight, 
  ChefHat,
  Plus,
  X,
  Sparkles,
  CheckCircle2,
  Settings,
  ChevronDown,
  ChevronUp,
  Clock,
  AlertTriangle,
  ChevronRight,
  Package,
  Zap
} from 'lucide-react';
import PantrySection from '@/components/PantrySection';
import BudgetSection from '@/components/BudgetSection';
import EmailDigestSection from '@/components/EmailDigestSection';
import HouseholdSection from '@/components/HouseholdSection';
import ShoppingPreferences from '@/components/ShoppingPreferences';
import PathSelector from '@/components/PathSelector';
import { OnboardingModal } from '@/components/OnboardingFlow';
import SessionRecoveryToast from '@/components/SessionRecoveryToast';
import { haptic, createSwipeHandlers } from '@/utils/haptics';

const Dashboard = () => {
  const { user, api, isAuthenticated } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    favorites: 0,
    groceryLists: 0,
    hasMealPlan: false,
    latestGroceryListId: null,
    mealPlanMeals: 0
  });
  const [recentFavorites, setRecentFavorites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showHouseholdSettings, setShowHouseholdSettings] = useState(false);
  const [pantryAlerts, setPantryAlerts] = useState([]);
  const [dismissedAlert, setDismissedAlert] = useState(false);
  const [budgetSummary, setBudgetSummary] = useState(null);
  const [showPathSelector, setShowPathSelector] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);
  
  // Demo mode state
  const isDemoMode = searchParams.get('demo') === 'true';
  const [demoStep, setDemoStep] = useState(1);
  const [showDemoBanner, setShowDemoBanner] = useState(isDemoMode);

  // Calculate if user has completed getting started
  const completedSteps = [
    stats.favorites > 0,
    stats.hasMealPlan,
    stats.groceryLists > 0
  ].filter(Boolean).length;
  const hasCompletedGettingStarted = completedSteps >= 3;

  useEffect(() => {
    const fetchDashboardData = async () => {
      if (!isAuthenticated) {
        setLoading(false);
        return;
      }
      
      try {
        const [favoritesRes, groceryListsRes, mealPlanRes, pantryRes, budgetRes] = await Promise.all([
          api.get('/favorites'),
          api.get('/grocery-lists'),
          api.get('/meal-plans'),
          api.get('/pantry').catch(() => ({ data: [] })),
          api.get('/budget').catch(() => ({ data: null }))
        ]);

        // Count meals in the meal plan
        let mealCount = 0;
        if (mealPlanRes.data?.days) {
          mealCount = mealPlanRes.data.days.reduce((acc, day) => acc + (day.meals?.length || 0), 0);
        }

        setStats({
          favorites: favoritesRes.data.length,
          groceryLists: groceryListsRes.data.length,
          hasMealPlan: !!mealPlanRes.data && mealCount > 0,
          latestGroceryListId: groceryListsRes.data[0]?.id || null,
          mealPlanMeals: mealCount
        });

        setRecentFavorites(favoritesRes.data.slice(0, 4));
        
        // Find expiring pantry items (within 3 days)
        const now = new Date();
        const expiring = (pantryRes.data || []).filter(item => {
          if (!item.expires_at) return false;
          const expDate = new Date(item.expires_at);
          const daysLeft = Math.ceil((expDate - now) / (1000 * 60 * 60 * 24));
          return daysLeft <= 3 && daysLeft >= 0;
        }).slice(0, 2);
        setPantryAlerts(expiring);
        
        // Budget summary
        if (budgetRes.data) {
          setBudgetSummary(budgetRes.data);
        }
      } catch (error) {
        console.error('Failed to fetch dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };

    // Check if user needs onboarding (first-time users)
    const checkOnboarding = async () => {
      if (!isAuthenticated || isDemoMode) return;
      
      try {
        const res = await api.get('/user/needs-onboarding');
        if (res.data.needs_onboarding) {
          // Check if there's a recommended path from pre-signup teaser
          const recommendedPath = sessionStorage.getItem('recommended_path');
          if (recommendedPath) {
            // User came from teaser, skip onboarding and go straight to path generation
            sessionStorage.removeItem('recommended_path');
            sessionStorage.removeItem('onboarding_goal');
            navigate('/meal-planner?path=' + recommendedPath);
          } else {
            setShowOnboarding(true);
          }
        }
      } catch (error) {
        console.error('Failed to check onboarding:', error);
      }
    };

    // Auto-open settings if coming from shopping preferences link
    if (searchParams.get('settings') === 'shopping') {
      setShowHouseholdSettings(true);
      // Clean up the URL
      searchParams.delete('settings');
      setSearchParams(searchParams, { replace: true });
    }

    fetchDashboardData();
    checkOnboarding();
  }, [api, isAuthenticated, isDemoMode, navigate, searchParams, setSearchParams]);

  const closeDemoMode = () => {
    setShowDemoBanner(false);
    searchParams.delete('demo');
    setSearchParams(searchParams);
  };

  const handleDemoAction = (step, path) => {
    navigate(`${path}?demo=true`);
  };

  const quickActions = [
    { title: 'Find Recipes', shortTitle: 'Search', icon: Search, to: '/recipes', demoStep: 1 },
    { title: 'Meal Planner', shortTitle: 'Plan', icon: Calendar, to: '/meal-planner', demoStep: 2 },
    { title: 'Grocery Lists', shortTitle: 'Lists', icon: ShoppingCart, to: '/grocery-lists', demoStep: 3 },
    { title: 'My Pantry', shortTitle: 'Pantry', icon: Package, to: '/pantry', demoStep: null }
  ];

  const gettingStartedSteps = [
    { step: 1, title: 'Find recipes you love', done: stats.favorites > 0, path: '/recipes' },
    { step: 2, title: 'Plan your week in minutes', done: stats.hasMealPlan, path: '/meal-planner' },
    { step: 3, title: 'Generate a shoppable grocery list', done: stats.groceryLists > 0, path: '/grocery-lists' }
  ];

  // Calculate days until expiry
  const getDaysUntilExpiry = (expiresAt) => {
    if (!expiresAt) return null;
    const now = new Date();
    const expDate = new Date(expiresAt);
    return Math.ceil((expDate - now) / (1000 * 60 * 60 * 24));
  };

  // Demo mode for unauthenticated users
  if (!isAuthenticated && isDemoMode) {
    return (
      <div className="min-h-screen py-8 pb-24 md:pb-8" data-testid="dashboard-demo-page">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Demo Banner */}
          <Card className="border-primary/50 bg-gradient-to-r from-primary/5 to-accent/5 mb-6">
            <CardContent className="py-4 md:py-6">
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-center gap-3 md:gap-4">
                  <div className="h-10 w-10 md:h-12 md:w-12 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Sparkles className="h-5 w-5 md:h-6 md:w-6 text-primary" />
                  </div>
                  <div>
                    <h2 className="text-lg md:text-xl font-semibold font-['Playfair_Display'] mb-0.5">
                      Welcome to Carted
                    </h2>
                    <p className="text-sm text-muted-foreground hidden sm:block">
                      Here&apos;s how to plan your week and send groceries to your cart in minutes.
                    </p>
                  </div>
                </div>
                <Button variant="ghost" size="icon" onClick={closeDemoMode} className="flex-shrink-0">
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </CardContent>
          </Card>

          <div className="mb-6">
            <h1 className="text-2xl md:text-4xl font-semibold font-['Playfair_Display'] mb-1">
              Your Dashboard Preview
            </h1>
            <p className="text-sm md:text-base text-muted-foreground">
              This is what your dashboard will look like.
            </p>
          </div>

          {/* Demo Quick Actions */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
            {quickActions.map((action, index) => (
              <Card 
                key={index} 
                className={`border-border/50 transition-all cursor-pointer group relative ${
                  action.demoStep === demoStep ? 'ring-2 ring-primary shadow-lg' : 'hover:border-primary/30'
                }`}
                onClick={() => setDemoStep(action.demoStep)}
              >
                {action.demoStep === demoStep && (
                  <div className="absolute -top-2 -left-2 h-5 w-5 rounded-full bg-primary text-primary-foreground text-xs flex items-center justify-center font-bold">
                    {action.demoStep}
                  </div>
                )}
                <CardContent className="p-4">
                  <div className="h-10 w-10 rounded-xl bg-primary/10 flex items-center justify-center mb-3">
                    <action.icon className="h-5 w-5 text-primary" />
                  </div>
                  <h3 className="font-medium text-sm mb-2">{action.title}</h3>
                  <Button 
                    size="sm" 
                    className="w-full text-xs h-8"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDemoAction(action.demoStep, action.to);
                    }}
                  >
                    Try It
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* CTA */}
          <div className="text-center py-8">
            <h3 className="text-xl md:text-2xl font-semibold font-['Playfair_Display'] mb-3">
              Ready to start planning?
            </h3>
            <Link to="/auth">
              <Button size="lg" className="rounded-full h-12 px-8">
                Get Started Free
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
        
        {/* Mobile Bottom Nav */}
        <MobileBottomNav actions={quickActions} />
      </div>
    );
  }

  return (
    <div className="min-h-screen py-6 md:py-8 pb-24 md:pb-8" data-testid="dashboard-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Demo Mode Banner */}
        {showDemoBanner && isAuthenticated && (
          <Card className="border-primary/50 bg-gradient-to-r from-primary/5 to-accent/5 mb-6">
            <CardContent className="py-4">
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-center gap-3">
                  <Sparkles className="h-5 w-5 text-primary flex-shrink-0" />
                  <p className="text-sm">
                    <span className="font-medium">Welcome!</span> Follow the steps to plan your week.
                  </p>
                </div>
                <Button variant="ghost" size="icon" onClick={closeDemoMode} className="h-8 w-8">
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Welcome - Compact on mobile */}
        <div className="mb-4 md:mb-6">
          <h1 className="text-2xl md:text-4xl font-semibold font-['Playfair_Display']">
            Welcome back{user?.name ? `, ${user.name.split(' ')[0]}` : ''}!
          </h1>
          <p className="text-sm md:text-base text-muted-foreground hidden md:block">
            What are we cooking today?
          </p>
        </div>

        {/* 🥇 1️⃣ PRIMARY ACTION - Paths or Context-Aware CTA */}
        {!stats.hasMealPlan && !showPathSelector ? (
          // No meal plan - Show Path intro card
          <Card className="border-primary/30 bg-gradient-to-br from-primary/5 via-background to-accent/5 mb-4 md:mb-8 overflow-hidden" data-testid="paths-intro-card">
            <CardContent className="py-5 md:py-8">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div className="flex items-center gap-3 md:gap-4">
                  <div className="h-12 w-12 md:h-14 md:w-14 rounded-2xl bg-gradient-to-br from-primary to-primary/80 flex items-center justify-center flex-shrink-0">
                    <Zap className="h-6 w-6 md:h-7 md:w-7 text-primary-foreground" />
                  </div>
                  <div>
                    <h2 className="text-lg md:text-2xl font-semibold font-['Playfair_Display'] mb-0.5">
                      Plan Your Week in One Tap
                    </h2>
                    <p className="text-sm text-muted-foreground">
                      Choose a style, we&apos;ll pick the meals
                    </p>
                  </div>
                </div>
                <Button 
                  size="default"
                  className="w-full sm:w-auto rounded-full h-10 md:h-12 px-6 md:px-8 active:scale-95 transition-transform" 
                  onClick={() => { haptic('medium'); setShowPathSelector(true); }} 
                  data-testid="choose-path-btn"
                >
                  Choose a Path
                  <Sparkles className="ml-2 h-4 w-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        ) : !stats.hasMealPlan && showPathSelector ? (
          // PathSelector expanded view
          <Card className="border-primary/20 mb-4 md:mb-8" data-testid="path-selector-card">
            <CardContent className="py-5 md:py-6">
              <PathSelector 
                onComplete={() => {
                  setShowPathSelector(false);
                  // Refresh stats
                  window.location.reload();
                }} 
              />
            </CardContent>
          </Card>
        ) : (
          // Has meal plan - Show context-aware CTA
          <Card className="border-primary/30 bg-gradient-to-r from-primary/5 via-background to-accent/5 mb-4 md:mb-8" data-testid="primary-action-card">
            <CardContent className="py-5 md:py-8">
              {/* Check for expiring pantry item first (mobile priority) */}
              {pantryAlerts.length > 0 && !dismissedAlert ? (
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                  <div className="flex items-center gap-3 md:gap-4">
                    <div className="h-12 w-12 md:h-14 md:w-14 rounded-2xl bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center flex-shrink-0">
                      <Clock className="h-6 w-6 md:h-7 md:w-7 text-amber-600 dark:text-amber-400" />
                    </div>
                    <div>
                      <h2 className="text-lg md:text-2xl font-semibold font-['Playfair_Display'] mb-0.5">
                        Use {pantryAlerts[0].name} Today
                      </h2>
                      <p className="text-sm text-muted-foreground">
                        {getDaysUntilExpiry(pantryAlerts[0].expires_at)} days left — we found recipes
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-2 w-full sm:w-auto">
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="md:hidden active:scale-95 transition-transform"
                      onClick={() => { haptic('light'); setDismissedAlert(true); }}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                    <Button 
                      size="default"
                      className="flex-1 sm:flex-none rounded-full h-10 md:h-12 px-6 md:px-8 active:scale-95 transition-transform" 
                      onClick={() => { haptic('medium'); navigate(`/recipes?query=${encodeURIComponent(pantryAlerts[0].name)}`); }} 
                      data-testid="use-ingredient-btn"
                    >
                      View Recipes
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ) : stats.groceryLists > 0 ? (
                // Option B: Has meal plan AND grocery list
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                  <div className="flex items-center gap-3 md:gap-4">
                    <div className="h-12 w-12 md:h-14 md:w-14 rounded-2xl bg-green-100 dark:bg-green-900/30 flex items-center justify-center flex-shrink-0">
                      <ShoppingCart className="h-6 w-6 md:h-7 md:w-7 text-green-600 dark:text-green-400" />
                    </div>
                    <div>
                      <h2 className="text-lg md:text-2xl font-semibold font-['Playfair_Display'] mb-0.5">
                        Your Grocery List Is Ready
                      </h2>
                      <p className="text-sm text-muted-foreground">
                        Export or review in one tap
                      </p>
                    </div>
                  </div>
                  <Button 
                    size="default"
                    className="w-full sm:w-auto rounded-full h-10 md:h-12 px-6 md:px-8 active:scale-95 transition-transform" 
                    onClick={() => { haptic('medium'); navigate(stats.latestGroceryListId ? `/grocery-lists/${stats.latestGroceryListId}` : '/grocery-lists'); }} 
                    data-testid="view-grocery-list-btn"
                  >
                    View Grocery List
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </div>
              ) : (
                // Option C: Has meal plan but no grocery list
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                  <div className="flex items-center gap-3 md:gap-4">
                    <div className="h-12 w-12 md:h-14 md:w-14 rounded-2xl bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center flex-shrink-0">
                      <ShoppingCart className="h-6 w-6 md:h-7 md:w-7 text-amber-600 dark:text-amber-400" />
                    </div>
                    <div>
                      <h2 className="text-lg md:text-2xl font-semibold font-['Playfair_Display'] mb-0.5">
                        Generate Your Grocery List
                      </h2>
                      <p className="text-sm text-muted-foreground">
                        {stats.mealPlanMeals} meals planned — create your list
                      </p>
                    </div>
                  </div>
                  <Button 
                    size="default"
                    className="w-full sm:w-auto rounded-full h-10 md:h-12 px-6 md:px-8 active:scale-95 transition-transform" 
                    onClick={() => { haptic('medium'); navigate('/meal-planner'); }} 
                    data-testid="generate-list-btn"
                  >
                    Generate List
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* ⚠️ 2️⃣ PANTRY ALERT BANNER - Mobile: under primary action */}
        {pantryAlerts.length > 0 && dismissedAlert && (
          <div 
            className="flex items-center justify-between p-3 mb-4 rounded-lg bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 cursor-pointer md:hidden active:bg-amber-100 transition-colors"
            onClick={() => { haptic('light'); navigate('/dashboard#pantry'); }}
            data-testid="pantry-alert-banner"
          >
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-amber-600" />
              <span className="text-sm font-medium text-amber-800 dark:text-amber-200">
                {pantryAlerts.length} item{pantryAlerts.length > 1 ? 's' : ''} expiring soon
              </span>
            </div>
            <ChevronRight className="h-4 w-4 text-amber-600" />
          </div>
        )}

        {/* 🗓️ 3️⃣ THIS WEEK SNAPSHOT - Mobile: Single compact strip */}
        <div 
          className="flex items-center justify-between p-3 mb-4 rounded-lg bg-muted/50 cursor-pointer md:hidden active:bg-muted transition-colors"
          onClick={() => { haptic('light'); navigate('/meal-planner'); }}
          data-testid="this-week-snapshot"
        >
          <div className="flex items-center gap-2">
            <Calendar className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm">
              <span className="font-medium">{stats.mealPlanMeals} meals</span> planned
              {stats.groceryLists > 0 && <span className="text-muted-foreground"> • {stats.groceryLists} list{stats.groceryLists > 1 ? 's' : ''} ready</span>}
            </span>
          </div>
          <ChevronRight className="h-4 w-4 text-muted-foreground" />
        </div>

        {/* Desktop Stats Row - Hidden on mobile */}
        <div className="hidden md:grid grid-cols-3 gap-4 mb-8">
          <Link to="/favorites" data-testid="stats-favorites">
            <Card className="border-border/50 hover:border-primary/30 transition-all cursor-pointer">
              <CardContent className="py-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-2xl font-bold">{stats.favorites}</p>
                    <p className="text-sm text-muted-foreground">Saved Recipes</p>
                  </div>
                  <Heart className="h-6 w-6 text-primary/40" />
                </div>
              </CardContent>
            </Card>
          </Link>
          <Link to="/grocery-lists" data-testid="stats-grocery-lists">
            <Card className="border-border/50 hover:border-primary/30 transition-all cursor-pointer">
              <CardContent className="py-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-2xl font-bold">{stats.groceryLists}</p>
                    <p className="text-sm text-muted-foreground">Grocery Lists</p>
                  </div>
                  <ShoppingCart className="h-6 w-6 text-primary/40" />
                </div>
              </CardContent>
            </Card>
          </Link>
          <Link to="/meal-planner" data-testid="stats-meal-plan">
            <Card className="border-border/50 hover:border-primary/30 transition-all cursor-pointer">
              <CardContent className="py-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-2xl font-bold">{stats.mealPlanMeals}</p>
                    <p className="text-sm text-muted-foreground">This Week&apos;s Plan</p>
                  </div>
                  <Calendar className="h-6 w-6 text-primary/40" />
                </div>
              </CardContent>
            </Card>
          </Link>
        </div>

        {/* 📦 6️⃣ PANTRY SECTION - Desktop: Full, Mobile: Collapsed alerts only */}
        <div className="mb-6 md:mb-8">
          <div className="hidden md:block">
            <PantrySection />
          </div>
          {/* Mobile: Show just expiring items summary with swipe-to-dismiss */}
          <div className="md:hidden">
            <MobilePantryAlerts 
              alerts={pantryAlerts} 
              onDismiss={() => setPantryAlerts([])} 
            />
          </div>
        </div>

        {/* ❤️ 4️⃣ RECENT FAVORITES - Horizontal Scroll */}
        {recentFavorites.length > 0 && (
          <div className="mb-6 md:mb-8">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-base md:text-lg font-semibold">Saved Recipes</h2>
              <Link to="/favorites">
                <Button variant="ghost" size="sm" className="h-8 text-xs" data-testid="view-all-favorites">
                  View All
                  <ArrowRight className="h-3 w-3 ml-1" />
                </Button>
              </Link>
            </div>
            <div className="flex gap-3 overflow-x-auto pb-2 -mx-4 px-4 md:mx-0 md:px-0 scrollbar-hide">
              {recentFavorites.map((fav) => (
                <Link 
                  key={fav.id} 
                  to={`/recipes/${fav.recipe_id}`}
                  className="flex-shrink-0 group"
                  data-testid={`favorite-${fav.recipe_id}`}
                >
                  <div className="w-28 md:w-32">
                    <img 
                      src={fav.recipe_image} 
                      alt={fav.recipe_title}
                      className="h-20 md:h-24 w-full rounded-lg object-cover group-hover:ring-2 ring-primary transition-all"
                    />
                    <p className="text-xs font-medium mt-2 line-clamp-2 group-hover:text-primary transition-colors">
                      {fav.recipe_title}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* ⚡ 5️⃣ QUICK ACTIONS - Desktop only (Mobile uses bottom bar) */}
        <div className="hidden md:block mb-8">
          <div className="flex items-center justify-between gap-2 p-1 bg-muted/50 rounded-xl">
            {quickActions.map((action, index) => (
              <Link 
                key={index} 
                to={action.to} 
                className="flex-1"
                data-testid={`action-${action.title.toLowerCase().replace(' ', '-')}`}
              >
                <Button 
                  variant="ghost" 
                  className={`w-full h-14 flex flex-col items-center justify-center gap-1 hover:bg-background hover:shadow-sm transition-all rounded-lg ${
                    showDemoBanner && action.demoStep === demoStep ? 'bg-background shadow-sm ring-2 ring-primary' : ''
                  }`}
                >
                  <action.icon className="h-5 w-5" />
                  <span className="text-xs font-medium">{action.title}</span>
                </Button>
              </Link>
            ))}
          </div>
        </div>

        {/* 4️⃣ GETTING STARTED - Conditional & Compact on mobile */}
        {!hasCompletedGettingStarted ? (
          <Card className="border-border/50 bg-gradient-to-br from-primary/5 to-accent/5 mb-6 md:mb-8">
            <CardHeader className="pb-2 pt-4 md:pt-6">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base md:text-lg font-['Playfair_Display']">Getting Started</CardTitle>
                <Badge variant="secondary" className="text-xs">{completedSteps}/3</Badge>
              </div>
            </CardHeader>
            <CardContent className="pb-4">
              <div className="space-y-2">
                {gettingStartedSteps.map((item) => (
                  <Link 
                    key={item.step} 
                    to={item.path}
                    className="flex gap-3 items-center p-2.5 rounded-lg hover:bg-background/50 transition-colors group"
                  >
                    <div className={`h-7 w-7 rounded-full flex items-center justify-center text-xs font-medium flex-shrink-0 ${
                      item.done 
                        ? 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400' 
                        : 'bg-muted text-muted-foreground'
                    }`}>
                      {item.done ? <CheckCircle2 className="h-4 w-4" /> : item.step}
                    </div>
                    <p className={`flex-1 text-sm font-medium ${item.done ? 'text-muted-foreground' : ''}`}>
                      {item.title}
                    </p>
                    {!item.done && (
                      <ChevronRight className="h-4 w-4 text-muted-foreground group-hover:text-primary" />
                    )}
                  </Link>
                ))}
              </div>
            </CardContent>
          </Card>
        ) : (
          <Card className="border-green-200 dark:border-green-800 bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-950/30 dark:to-emerald-950/30 mb-6 md:mb-8" data-testid="all-set-banner">
            <CardContent className="py-4 md:py-6">
              <div className="flex items-center gap-3 md:gap-4">
                <div className="h-10 w-10 md:h-12 md:w-12 rounded-full bg-green-100 dark:bg-green-900/50 flex items-center justify-center flex-shrink-0">
                  <CheckCircle2 className="h-5 w-5 md:h-6 md:w-6 text-green-600 dark:text-green-400" />
                </div>
                <div>
                  <h3 className="font-semibold text-green-800 dark:text-green-200">You&apos;re all set!</h3>
                  <p className="text-sm text-green-700 dark:text-green-300">
                    Your meals, lists, and pantry stay in sync.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* 💸 7️⃣ BUDGET - Desktop: Full Section, Mobile: Insight only */}
        <div className="hidden md:block mb-8">
          <BudgetSection />
        </div>
        {budgetSummary && (
          <MobileBudgetInsight budget={budgetSummary} />
        )}

        {/* ⚙️ 8️⃣ HOUSEHOLD & SETTINGS - Desktop only */}
        <div className="hidden md:block mb-8">
          <button 
            onClick={() => setShowHouseholdSettings(!showHouseholdSettings)}
            className="flex items-center justify-between w-full text-left mb-4 group"
            data-testid="household-settings-toggle"
          >
            <div className="flex items-center gap-2">
              <Settings className="h-5 w-5 text-muted-foreground" />
              <h2 className="text-lg font-semibold font-['Playfair_Display']">Household & Settings</h2>
            </div>
            {showHouseholdSettings ? (
              <ChevronUp className="h-5 w-5 text-muted-foreground" />
            ) : (
              <ChevronDown className="h-5 w-5 text-muted-foreground" />
            )}
          </button>
          
          {showHouseholdSettings && (
            <div className="space-y-6 pl-7 border-l-2 border-muted">
              <ShoppingPreferences />
              <EmailDigestSection />
              <HouseholdSection />
            </div>
          )}
        </div>
      </div>
      
      {/* ⚡ 5️⃣ MOBILE BOTTOM ACTION BAR */}
      <MobileBottomNav actions={quickActions} />
      
      {/* Session Recovery Toast for users with incomplete sessions */}
      {!showOnboarding && !isDemoMode && <SessionRecoveryToast />}
      
      {/* Onboarding Modal for first-time users */}
      <OnboardingModal 
        open={showOnboarding} 
        onOpenChange={setShowOnboarding}
        onComplete={() => setShowOnboarding(false)}
      />
    </div>
  );
};

// Mobile Bottom Navigation Bar Component
const MobileBottomNav = ({ actions }) => {
  const navigate = useNavigate();
  
  const handleNavClick = (to) => {
    haptic('medium');
    navigate(to);
  };
  
  return (
    <div className="fixed bottom-0 left-0 right-0 bg-background/95 backdrop-blur-sm border-t border-border md:hidden z-50" data-testid="mobile-bottom-nav">
      <div className="flex items-center justify-around py-2 px-4 max-w-md mx-auto">
        {actions.map((action, index) => (
          <button
            key={index}
            onClick={() => handleNavClick(action.to)}
            className="flex flex-col items-center justify-center p-2 min-w-[60px] rounded-lg hover:bg-muted active:scale-95 transition-all"
            data-testid={`mobile-nav-${action.shortTitle.toLowerCase()}`}
          >
            <action.icon className="h-5 w-5 text-muted-foreground" />
            <span className="text-[10px] mt-1 text-muted-foreground font-medium">{action.shortTitle}</span>
          </button>
        ))}
      </div>
    </div>
  );
};

// Mobile Pantry Alerts Component with Swipe-to-Dismiss
const MobilePantryAlerts = ({ alerts, onDismiss }) => {
  const navigate = useNavigate();
  const [translateX, setTranslateX] = useState(0);
  const [isDismissing, setIsDismissing] = useState(false);
  
  if (alerts.length === 0 || isDismissing) return null;
  
  const handleSwipe = (direction) => {
    if (direction === 'left' || direction === 'right') {
      setIsDismissing(true);
      haptic('success');
      setTimeout(() => onDismiss?.(), 300);
    }
  };
  
  const swipeHandlers = createSwipeHandlers(
    () => handleSwipe('left'),
    () => handleSwipe('right'),
    { threshold: 80 }
  );
  
  // Track swipe position for visual feedback
  const handleTouchMove = (e) => {
    const touch = e.touches[0];
    const startX = e.target.dataset.startX;
    if (startX) {
      const diff = touch.clientX - parseFloat(startX);
      setTranslateX(Math.max(-100, Math.min(100, diff)));
    }
  };
  
  const handleTouchStart = (e) => {
    e.target.dataset.startX = e.touches[0].clientX;
    swipeHandlers.onTouchStart(e);
  };
  
  const handleTouchEnd = (e) => {
    setTranslateX(0);
    delete e.target.dataset.startX;
    swipeHandlers.onTouchEnd(e);
  };
  
  return (
    <div 
      className={`transition-all duration-300 ${isDismissing ? 'opacity-0 scale-95' : 'opacity-100'}`}
      style={{ transform: `translateX(${translateX}px)` }}
    >
      <Card 
        className="border-amber-200 dark:border-amber-800 bg-amber-50/50 dark:bg-amber-950/20 touch-pan-y"
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        data-testid="swipeable-pantry-alert"
      >
        <CardContent className="py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-amber-600" />
              <div>
                <p className="text-sm font-medium text-amber-800 dark:text-amber-200">
                  {alerts.length} item{alerts.length > 1 ? 's' : ''} expiring soon
                </p>
                <p className="text-xs text-amber-600 dark:text-amber-400">
                  {alerts.map(a => a.name).join(', ')}
                </p>
              </div>
            </div>
            <Button 
              variant="ghost" 
              size="sm" 
              className="h-8 text-amber-700"
              onClick={() => {
                haptic('light');
                navigate('/dashboard#pantry');
              }}
            >
              View
              <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          </div>
          <p className="text-[10px] text-amber-500 text-center mt-2 md:hidden">
            Swipe to dismiss
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

// Mobile Budget Insight Component
const MobileBudgetInsight = ({ budget }) => {
  const navigate = useNavigate();
  
  // Calculate remaining budget
  const weeklyBudget = budget?.weekly_budget || 0;
  const weeklySpent = budget?.weekly_spent || 0;
  const remaining = weeklyBudget - weeklySpent;
  const isUnderBudget = remaining >= 0;
  
  if (!weeklyBudget) return null;
  
  return (
    <div 
      className="flex items-center justify-between p-3 mb-4 rounded-lg bg-muted/50 cursor-pointer md:hidden"
      onClick={() => navigate('/dashboard#budget')}
      data-testid="mobile-budget-insight"
    >
      <div className="flex items-center gap-2">
        <span className="text-lg">💰</span>
        <div>
          <span className="text-sm font-medium">${remaining.toFixed(0)} left this week</span>
          <span className="text-xs text-muted-foreground ml-2">
            {isUnderBudget ? '👍' : '⚠️'}
          </span>
        </div>
      </div>
      <ChevronRight className="h-4 w-4 text-muted-foreground" />
    </div>
  );
};

export default Dashboard;
